import { Routes } from "@angular/router";
import { LvfAppModule, LvfTrainingSessionParticipantRole } from "@kiraah-shared/common";
import {
  MOBILE_MENTOR_PORTAL_AGGREGATE_DOCUMENTS,
  MOBILE_MENTOR_PORTAL_AGGREGATE_TASKS,
  MOBILE_CONTACT_PORTAL_AGGREGATE_DOCUMENTS,
  MOBILE_CONTACT_PORTAL_AGGREGATE_TASKS,
  MOBILE_STUDENT_PORTAL_AGGREGATE_DOCUMENTS,
  MOBILE_STUDENT_PORTAL_AGGREGATE_TASKS,
  MOBILE_TRAINING_SESSION_HUB_LIST,
  MOBILE_TRAINING_SESSION_LVF_MODULE,
  MOBILE_TRAINING_SESSION_PARTICIPANT_ROLE,
  MOBILE_TRAINING_SESSION_ROUTE_SEGMENTS,
  type MobileTrainingSessionRouteSegments,
} from "./mobile-training-session-route.provider";

const trainingSessionChildRoutes: Routes = [
  {
    path: "",
    pathMatch: "full",
    loadComponent: () =>
      import("./mobile-training-session-list-page/mobile-training-session-list-page.component").then(
        (moduleExports) => moduleExports.MobileTrainingSessionListPageComponent
      ),
  },
  {
    path: ":sessionUid/documents",
    loadComponent: () =>
      import("./mobile-training-session-documents-page/mobile-training-session-documents-page.component").then(
        (moduleExports) => moduleExports.MobileTrainingSessionDocumentsPageComponent
      ),
  },
  {
    path: ":sessionUid/info",
    loadComponent: () =>
      import("./mobile-training-session-info-page/mobile-training-session-info-page.component").then(
        (moduleExports) => moduleExports.MobileTrainingSessionInfoPageComponent
      ),
  },
  {
    path: ":sessionUid/tasks",
    loadComponent: () =>
      import("./mobile-training-session-tasks-page/mobile-training-session-tasks-page.component").then(
        (moduleExports) => moduleExports.MobileTrainingSessionTasksPageComponent
      ),
  },
  {
    path: ":sessionUid/t/:taskUid/chat",
    loadComponent: () =>
      import("../mission-task/mobile-task-chat-page/mobile-task-chat-page.component").then(
        (m) => m.MobileTaskChatPageComponent
      ),
  },
  {
    path: ":sessionUid/t/:taskUid",
    loadComponent: () =>
      import("../mission-task/mobile-task-edit-page/mobile-task-edit-page.component").then(
        (moduleExports) => moduleExports.MobileTaskEditPageComponent
      ),
  },
  {
    path: ":sessionUid/ai-questionnaire/result/:transcriptUid",
    loadComponent: () =>
      import("../ai-questionnaire/mobile-ai-questionnaire-result-page/mobile-ai-questionnaire-result-page.component").then(
        (moduleExports) => moduleExports.MobileAiQuestionnaireResultPageComponent
      ),
  },
  {
    path: ":sessionUid/ai-questionnaire",
    loadComponent: () =>
      import("../ai-questionnaire/mobile-ai-questionnaire-workflow-page/mobile-ai-questionnaire-workflow-page.component").then(
        (moduleExports) => moduleExports.MobileAiQuestionnaireWorkflowPageComponent
      ),
  },
  {
    path: ":sessionUid/voice-quiz/result/:transcriptUid",
    redirectTo: ":sessionUid/ai-questionnaire/result/:transcriptUid",
  },
  {
    path: ":sessionUid/voice-quiz",
    redirectTo: ":sessionUid/ai-questionnaire",
  },
  {
    path: ":sessionUid",
    loadComponent: () =>
      import("./mobile-training-session-detail-page/mobile-training-session-detail-page.component").then(
        (moduleExports) => moduleExports.MobileTrainingSessionDetailPageComponent
      ),
  },
];

function trainingSessionShellRoutes(
  segments: MobileTrainingSessionRouteSegments,
  participantRole: LvfTrainingSessionParticipantRole,
  permissionModule: LvfAppModule,
  hubList = false
): Routes {
  return [
    {
      path: "",
      providers: [
        { provide: MOBILE_TRAINING_SESSION_ROUTE_SEGMENTS, useValue: segments },
        { provide: MOBILE_TRAINING_SESSION_PARTICIPANT_ROLE, useValue: participantRole },
        { provide: MOBILE_TRAINING_SESSION_LVF_MODULE, useValue: permissionModule },
        { provide: MOBILE_TRAINING_SESSION_HUB_LIST, useValue: hubList },
      ],
      children: trainingSessionChildRoutes,
    },
  ];
}

/** Hub E-Satisfaction → `/mobile/elearning/sessions` (aligné web apprenant). */
export const authenticatedHubTrainingSessionRoutes = trainingSessionShellRoutes(
  ["sessions"],
  LvfTrainingSessionParticipantRole.STUDENT,
  LvfAppModule.ELEARNING_STUDENT_SESSION,
  true
);

/** Apprenant → `/mobile/elearning/student/sessions` (aligné web `StudentTrainingSessionListComponent`). */
export const authenticatedStudentTrainingSessionRoutes = trainingSessionShellRoutes(
  ["student", "sessions"],
  LvfTrainingSessionParticipantRole.STUDENT,
  LvfAppModule.ELEARNING_STUDENT_SESSION
);

/** Formateur → `/mobile/elearning/mentor/sessions` (aligné web `MentorTrainingSessionListComponent`). */
export const authenticatedMentorTrainingSessionRoutes = trainingSessionShellRoutes(
  ["mentor", "sessions"],
  LvfTrainingSessionParticipantRole.MENTOR,
  LvfAppModule.ELEARNING_MENTOR_SESSION
);

/** Formateur → `/mobile/elearning/mentor/tasks` (aligné web `MentorTaskListComponent`). */
export const authenticatedMentorPortalTasksRoutes: Routes = [
  {
    path: "",
    providers: [
      { provide: MOBILE_TRAINING_SESSION_ROUTE_SEGMENTS, useValue: ["mentor", "sessions"] as const },
      {
        provide: MOBILE_TRAINING_SESSION_PARTICIPANT_ROLE,
        useValue: LvfTrainingSessionParticipantRole.MENTOR,
      },
      { provide: MOBILE_TRAINING_SESSION_LVF_MODULE, useValue: LvfAppModule.ELEARNING_MENTOR_SESSION },
      { provide: MOBILE_MENTOR_PORTAL_AGGREGATE_TASKS, useValue: true },
    ],
    loadComponent: () =>
      import("./mobile-training-session-tasks-page/mobile-training-session-tasks-page.component").then(
        (moduleExports) => moduleExports.MobileTrainingSessionTasksPageComponent
      ),
  },
];

/** Apprenant → `/mobile/elearning/student/tasks`. */
export const authenticatedStudentPortalTasksRoutes: Routes = [
  {
    path: "",
    providers: [
      { provide: MOBILE_TRAINING_SESSION_ROUTE_SEGMENTS, useValue: ["student", "sessions"] as const },
      {
        provide: MOBILE_TRAINING_SESSION_PARTICIPANT_ROLE,
        useValue: LvfTrainingSessionParticipantRole.STUDENT,
      },
      { provide: MOBILE_TRAINING_SESSION_LVF_MODULE, useValue: LvfAppModule.ELEARNING_STUDENT_SESSION },
      { provide: MOBILE_STUDENT_PORTAL_AGGREGATE_TASKS, useValue: true },
    ],
    loadComponent: () =>
      import("./mobile-training-session-tasks-page/mobile-training-session-tasks-page.component").then(
        (moduleExports) => moduleExports.MobileTrainingSessionTasksPageComponent
      ),
  },
];

/** Apprenant → `/mobile/elearning/student/documents`. */
export const authenticatedStudentPortalDocumentsRoutes: Routes = [
  {
    path: "",
    providers: [
      { provide: MOBILE_TRAINING_SESSION_ROUTE_SEGMENTS, useValue: ["student", "sessions"] as const },
      {
        provide: MOBILE_TRAINING_SESSION_PARTICIPANT_ROLE,
        useValue: LvfTrainingSessionParticipantRole.STUDENT,
      },
      { provide: MOBILE_TRAINING_SESSION_LVF_MODULE, useValue: LvfAppModule.ELEARNING_STUDENT_SESSION },
      { provide: MOBILE_STUDENT_PORTAL_AGGREGATE_DOCUMENTS, useValue: true },
    ],
    loadComponent: () =>
      import("./mobile-training-session-documents-page/mobile-training-session-documents-page.component").then(
        (moduleExports) => moduleExports.MobileTrainingSessionDocumentsPageComponent
      ),
  },
];

/** Formateur → `/mobile/elearning/mentor/documents` (aligné web `MentorDocumentListComponent`). */
export const authenticatedMentorPortalDocumentsRoutes: Routes = [
  {
    path: "",
    providers: [
      { provide: MOBILE_TRAINING_SESSION_ROUTE_SEGMENTS, useValue: ["mentor", "sessions"] as const },
      {
        provide: MOBILE_TRAINING_SESSION_PARTICIPANT_ROLE,
        useValue: LvfTrainingSessionParticipantRole.MENTOR,
      },
      { provide: MOBILE_TRAINING_SESSION_LVF_MODULE, useValue: LvfAppModule.ELEARNING_MENTOR_SESSION },
      { provide: MOBILE_MENTOR_PORTAL_AGGREGATE_DOCUMENTS, useValue: true },
    ],
    loadComponent: () =>
      import("./mobile-training-session-documents-page/mobile-training-session-documents-page.component").then(
        (moduleExports) => moduleExports.MobileTrainingSessionDocumentsPageComponent
      ),
  },
];

/** Contact → `/mobile/elearning/contact/sessions` (aligné web `ContactTrainingSessionListComponent`). */
export const authenticatedContactTrainingSessionRoutes = trainingSessionShellRoutes(
  ["contact", "sessions"],
  LvfTrainingSessionParticipantRole.THIRD_PART_CONTACT,
  LvfAppModule.ELEARNING_THIRD_PART_CONTACT_SESSION
);

/** Entreprise → `/mobile/elearning/contact/tasks`. */
export const authenticatedContactPortalTasksRoutes: Routes = [
  {
    path: "",
    providers: [
      { provide: MOBILE_TRAINING_SESSION_ROUTE_SEGMENTS, useValue: ["contact", "sessions"] as const },
      {
        provide: MOBILE_TRAINING_SESSION_PARTICIPANT_ROLE,
        useValue: LvfTrainingSessionParticipantRole.THIRD_PART_CONTACT,
      },
      {
        provide: MOBILE_TRAINING_SESSION_LVF_MODULE,
        useValue: LvfAppModule.ELEARNING_THIRD_PART_CONTACT_SESSION,
      },
      { provide: MOBILE_CONTACT_PORTAL_AGGREGATE_TASKS, useValue: true },
    ],
    loadComponent: () =>
      import("./mobile-training-session-tasks-page/mobile-training-session-tasks-page.component").then(
        (moduleExports) => moduleExports.MobileTrainingSessionTasksPageComponent
      ),
  },
];

/** Entreprise → `/mobile/elearning/contact/documents`. */
export const authenticatedContactPortalDocumentsRoutes: Routes = [
  {
    path: "",
    providers: [
      { provide: MOBILE_TRAINING_SESSION_ROUTE_SEGMENTS, useValue: ["contact", "sessions"] as const },
      {
        provide: MOBILE_TRAINING_SESSION_PARTICIPANT_ROLE,
        useValue: LvfTrainingSessionParticipantRole.THIRD_PART_CONTACT,
      },
      {
        provide: MOBILE_TRAINING_SESSION_LVF_MODULE,
        useValue: LvfAppModule.ELEARNING_THIRD_PART_CONTACT_SESSION,
      },
      { provide: MOBILE_CONTACT_PORTAL_AGGREGATE_DOCUMENTS, useValue: true },
    ],
    loadComponent: () =>
      import("./mobile-training-session-documents-page/mobile-training-session-documents-page.component").then(
        (moduleExports) => moduleExports.MobileTrainingSessionDocumentsPageComponent
      ),
  },
];
